<template>
  <v-dialog v-model="open" width="650px">
    <v-card dense>
      <v-toolbar elevation="0">
        <v-toolbar-title
          class="text--subtitle error--text"
          style="padding-left: 10px"
        >
          {{ $t('message.pages.video.mutedIndicator.delete.title') }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn :disabled="isFetching" icon @click="handleClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text class="px-7 py-0">
        <v-row no-gutters>
          <!-- default message -->
          <v-col cols="12">
            {{ $t('message.pages.video.mutedIndicator.delete.baseMessage') }}
          </v-col>

          <!-- confirm::remove -->
          <template v-if="!isConfirmingPassword">
            <v-col cols="12" class="my-3">
              <b>{{
                $t('message.pages.video.deleteModal.confirmRemoveMessage2')
              }}</b>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model.trim="form.password"
                :label="$t('message.password')"
                :error="errors.state.invalidAccountError"
                :messages="loginPasswordErrorMessage"
                :append-icon="form.showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="form.showPassword ? 'text' : 'password'"
                autocomplete="off"
                class="pr-2"
                outlined
                dense
                @click:append="form.showPassword = !form.showPassword"
              >
                <template v-slot:message="{ message }">
                  <span
                    v-if="errors.state.invalidAccountError"
                    class="red--text caption align-text"
                  >
                    <v-icon color="red" size="18" class="pb-1">
                      mdi-alert-circle
                    </v-icon>
                    {{ message }}
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-checkbox v-model="form.confirm" class="mt-0 pt-0" hide-details>
                <template v-slot:label>
                  <span style="font-size: 14px">
                    {{ $t('message.pages.video.deleteModal.checkboxText') }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
          </template>

          <!-- confirm::password -->
          <template v-if="isConfirmingPassword">
            <v-col cols="12" class="my-3">
              {{
                $t('message.pages.video.mutedIndicator.delete.confirmMessage')
              }}
            </v-col>
          </template>
        </v-row>
      </v-card-text>

      <!-- error message -->
      <template v-if="errors.removeError">
        <v-col cols="12" class="mt-0 pt-0">
          <v-card
            class="d-flex align-center justify-start ml-5"
            width="100%"
            height="20px"
            flat
          >
            <span class="error--text caption">
              {{ $t('message.pages.video.deleteModal.errors.removeError') }}
              <br />
              {{ this.errors.message }}
            </span>
          </v-card>
        </v-col>
      </template>

      <v-card-actions class="px-7 pb-6">
        <v-spacer />
        <template v-if="!isConfirmingPassword">
          <base-btn
            :disabled="isFetching"
            type="tertiary"
            class="mr-3"
            @click="handleClose"
          >
            <span>{{ $t('message.cancel') }}</span>
          </base-btn>
          <base-btn
            :loading="isFetching"
            :disabled="isFetching || !form.confirm || !form.password"
            @click="handleRemove"
          >
            <span class="px-5">{{ $t('message.confirm') }}</span>
          </base-btn>
        </template>

        <template v-if="isConfirmingPassword">
          <v-progress-circular
            v-if="isFetching"
            :size="24"
            width="3"
            color="primary"
            indeterminate
          />
          <template v-else>
            <base-btn class="mx-3" type="secondary" @click="removeFromLibrary">
              {{
                $t(
                  'message.pages.video.mutedIndicator.delete.removeFromLibrary'
                )
              }}
            </base-btn>
            <base-btn @click="removeFromAll">
              {{
                $t('message.pages.video.mutedIndicator.delete.removeFromAll')
              }}
            </base-btn>
          </template>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// external imports
import { mapState, mapActions } from 'vuex'

// internal imports

// API
import imagesAPI from '@/services/imagesApi.js'
import personalizedAPI from '@/services/personalizedApi.js'

// Components

export default {
  name: 'MutedCustomDelete',
  components: {
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        item: {},
        open: false,
      }),
      required: true,
    },
  },
  inject: ['konva'],
  data: () => ({
    form: {
      password: '',
      confirm: false,
      showPassword: false,
    },
    confirming: 'remove',
    isFetching: false,
    errors: {
      state: {
        invalidAccountError: false,
        unexpectedError: false,
        internalErrorException: false,
        removeError: false,
        unknownError: '',
      },
      message: '',
    },
  }),
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
    }),
    open() {
      return this.value.open
    },
    item() {
      return this.value.item
    },
    userInfo() {
      return this.$store.getters['session/userAttributes']
    },
    isConfirmingPassword() {
      return this.confirming === 'password'
    },
    loginPasswordErrorMessage() {
      const i18n = this.$i18next
      let message = ''

      if (this.errors.state.invalidAccountError) {
        message = i18n.t(
          'message.pages.video.deleteModal.errors.invalidAccountError'
        )
      }

      if (this.errors.state.internalErrorException) {
        message = i18n.t('message.pages.video.deleteModal.errors.internalError')
      }

      if (this.errors.state.unexpectedError) {
        message = `${i18n.t('message.errors.unexpectedError')} ${
          this.errors.state.unknownError
        }`
      }

      return message
    },
    konvaConfig() {
      return this.konva
    },
  },
  methods: {
    ...mapActions('session', ['signInUser']),
    async handleRemove() {
      if (this.isConfirmingPassword) return

      const confirmResponse = await this.confirmUserPassword()

      if (!confirmResponse) return

      this.confirming = 'password'
    },
    removeFromLibrary() {
      const isRemoving = false
      this.deleteMutedCustom(isRemoving)
    },
    removeFromAll() {
      const isRemoving = true
      this.deleteMutedCustom(isRemoving)
    },
    async deleteMutedCustom(isRemoving) {
      this.isFetching = true
      this.errors.unknownError = ''

      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))

      try {
        const { id, metadata } = this.item
        const isDeletingCurrentCustom =
          id === this.profile.vsl.customMutedIndicatorId
        const isLibEmpty = this.konvaConfig.lib.length - 1 <= 0
        await personalizedAPI.deletePersonalized(id, { remove: isRemoving })
        if (isRemoving) await imagesAPI.deleteImage(metadata.imgID)
        if ((isDeletingCurrentCustom || isLibEmpty) && isRemoving) {
          this.$emit('removing-current')
        }

        this.handleClose()
      } catch (e) {
        this.errors.message =
          e.response?.data.errMsg.message + '. ' + e.response?.data.errCode
        this.errors.removeError = true
      } finally {
        this.isFetching = false
      }
    },
    async confirmUserPassword() {
      const username = this.userInfo.email
      const { password } = this.form

      this.isFetching = true

      try {
        await this.signInUser({ username, password })
        this.isFetching = false
        return true
      } catch (error) {
        const hasNotCodeError = !error.code
        const isNotAuthOrInvalidParam = [
          'NotAuthorizedException',
          'InvalidParameterException',
        ].includes(error.code)
        const isInternalException = ['InternalErrorException'].includes(
          error.code
        )

        if (hasNotCodeError) {
          const anyError = JSON.parse(JSON.stringify(error))
          if (anyError.message.includes('Network Error')) {
            this.changePasswordLoading = false
            this.errors.state.unknownError = 'Code: 500'
            return
          }
        }

        if (isNotAuthOrInvalidParam) {
          this.errors.state.invalidAccountError = true
        }

        if (isInternalException) {
          this.errors.state.internalErrorException = true
        }

        this.isFetching = false

        return false
      }
    },
    resetData() {
      this.form.password = ''
      this.form.confirm = false
      this.form.showPassword = false

      this.confirming = 'remove'
      this.isFetching = false

      this.errors.state.invalidAccountError = false
      this.errors.state.unexpectedError = false
      this.errors.state.internalErrorException = false
      this.errors.state.removeError = false
      this.errors.state.unknownError = ''
      this.errors.message = ''
    },
    handleClose() {
      this.$emit('close')
      this.resetData()
    },
  },
}
</script>
